import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Radio } from '../radio';
import { Checkbox } from '../checkbox';
import { useClickOutside } from '../hooks/useClickOutside';

type AllowedPosition = 'LEFT' | 'RIGHT';

const Container = styled.div<{ $disabled?: boolean; $hasSelection?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0 10px;
    height: 28px;
    box-sizing: border-box;
    border-radius: 6px;
    border: 1px solid ${(props) => (props.$hasSelection ? '#E8650A' : '#e5e7eb')};
    background: ${(props) => (props.$hasSelection ? '#fff7ed' : '#fff')};
    font-size: 12px;
    font-weight: 500;
    color: ${(props) => (props.$hasSelection ? '#E8650A' : '#263238')};
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;
    white-space: nowrap;
    position: relative;
    opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
    pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};

    &:hover {
        border-color: ${(props) => (props.$hasSelection ? '#ea6c0b' : '#d1d5db')};
    }

    .material-symbols-outlined {
        font-size: 16px;
    }
`;

const ActiveBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    border-radius: 8px;
    background: #dc6843;
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    padding: 0 4px;
`;

const Label = styled.div`
    user-select: none;
    display: flex;
    align-items: center;
    gap: 4px;

    > span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const DropdownArrow = styled.span`
    font-size: 14px !important;
    color: inherit;
    line-height: 1;
    display: flex;
    align-items: center;
`;

const Popover = styled.div<{ $width?: string; $position?: AllowedPosition }>`
    z-index: 50;
    position: absolute;
    width: auto;
    ${(props) => props.$width && `min-width: ${props.$width};`}
    ${(props) => (!props.$position || props.$position === 'LEFT') && `left: 0;`}
	${(props) => props.$position && props.$position === 'RIGHT' && `right: 0;`}
	top: calc(100% + 6px);
`;

const FilterRadio = styled(Radio as typeof Radio<string>)`
    label {
        font-size: 12px;
        font-weight: 400;
    }
`;

const PopoverBody = styled.div`
    padding: 6px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    gap: 1px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const List = styled.div<{ $maxHeight?: string }>`
    display: flex;
    flex-direction: column;
    gap: 2px;
    ${(props) => props.$maxHeight && `max-height: ${props.$maxHeight}; overflow-y: auto;`}
`;

const ListItem = styled.div<{ $active?: boolean; $rich?: boolean }>`
    list-style: none;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    transition: all 0.12s ease;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: ${(props) => (props.$active ? '#E8650A' : '#263238')};
    background: ${(props) => (props.$active ? '#fff7ed' : 'none')};
    user-select: none;

    &:hover {
        background: ${(props) => (props.$active ? '#ffedd5' : '#f3f4f6')};
    }
`;

const ClearFooter = styled.div`
    padding: 6px 8px;
    border-top: 1px solid #f3f4f6;
    margin-top: 2px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #dc2626;
    cursor: pointer;
    text-align: left;

    &:hover {
        text-decoration: underline;
    }
`;

const StyledCheckboxLabel = styled.span`
    align-self: center;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #263238;
`;

interface IOption<T> {
    label: string | React.ReactNode;
    value: T;
    chipLabel?: string;
}

export type SelectionValue<T> = T | T[] | null;

export interface ISelectOptionsProps<T> {
    options: IOption<string>[];
    placeholder: string;
    value?: SelectionValue<T>;
    onChange?: (value: SelectionValue<T>) => void;
    width?: string;
    maxHeight?: string;
    position?: AllowedPosition;
    multiple?: boolean;
    clearable?: boolean;
    updateLabel?: boolean;
    disabled?: boolean;
    disabledTooltip?: string;
    showCheckbox?: boolean;
}

export const SelectOptions = <T = string,>({
    value,
    options,
    placeholder,
    width,
    position,
    onChange,
    maxHeight,
    multiple = true,
    clearable = true,
    updateLabel = false,
    disabled = false,
    disabledTooltip = '',
    showCheckbox = true
}: ISelectOptionsProps<T>) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);

    useClickOutside(popoverRef, () => setIsVisible(false), containerRef);

    const [_innerState, _setInnerState] = useState<SelectionValue<T>>([]);
    const [isVisible, setIsVisible] = useState<boolean>(false);

    useEffect(() => {
        const normalizedValue = Array.isArray(value) ? value.map((v) => String(v)) : value != null ? String(value) : multiple ? [] : null;

        const isEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);
        if (!isEqual(_innerState, normalizedValue)) {
            _setInnerState(normalizedValue as SelectionValue<T>);
        }
    }, [value]);

    const handleInputChange = (value: T, remove = false) => {
        if (!multiple) {
            onChange?.(value);
            return _setInnerState(value);
        }

        // we should not call onChange in this function since it should remain pure,
        // however we are trying to not add dependencies on the _internalState in order
        // to avoid possible infinite loops.
        _setInnerState((current) => {
            if (!Array.isArray(current)) {
                onChange?.(value);
                return value;
            }

            if (remove) {
                const index = current.indexOf(value);
                if (index === -1) return current;
                const tempCurrent = [...current];
                tempCurrent.splice(index, 1);
                const updated = [...tempCurrent];
                onChange?.(updated);
                return updated;
            }

            const updated = [...current, value];
            onChange?.(updated);
            return updated;
        });
    };

    const handleClearing = () => {
        _setInnerState(multiple ? [] : null);
        onChange?.(multiple ? [] : null);
    };

    const hasSelection = multiple ? Array.isArray(_innerState) && _innerState.length > 0 : _innerState != null && _innerState !== '';

    const selectionCount = multiple ? (Array.isArray(_innerState) ? _innerState.length : 0) : hasSelection ? 1 : 0;

    const label = useMemo(() => {
        if (!updateLabel || !_innerState || (Array.isArray(_innerState) && _innerState.length === 0)) return placeholder;

        if (!multiple) return options.find((o) => o.value === _innerState)?.label ?? placeholder;

        return (_innerState as string[])
            .map((val) => options.find((o) => o.value === val)?.label)
            .filter(Boolean)
            .join(', ');
    }, [_innerState, placeholder, updateLabel]);

    const SelectContent = (
        <Container
            ref={containerRef}
            $disabled={disabled}
            $hasSelection={hasSelection}
            onClick={(ev): void => {
                if (disabled) return;
                ev.stopPropagation();
                setIsVisible((v) => !v);
            }}
        >
            <Label>
                <span>
                    {hasSelection
                        ? (() => {
                              const getChipLabel = (val: unknown) => {
                                  const opt = options.find((o) => o.value === val);
                                  return opt?.chipLabel || (typeof opt?.label === 'string' ? opt.label : null);
                              };
                              if (!multiple) {
                                  const name = getChipLabel(_innerState);
                                  return name ? `${placeholder}: ${name}` : placeholder;
                              }
                              const selected = ((_innerState as string[]) || []).map(getChipLabel).filter(Boolean);
                              if (selected.length === 0) return placeholder;
                              if (selected.length === 1) return `${placeholder}: ${selected[0]}`;
                              return `${placeholder}: ${selected[0]} +${selected.length - 1}`;
                          })()
                        : placeholder}
                </span>
                <DropdownArrow className='material-symbols-outlined' aria-hidden='true'>
                    expand_more
                </DropdownArrow>
            </Label>
            {isVisible && (
                <Popover $width={width} $position={position} ref={popoverRef}>
                    <PopoverBody>
                        <List $maxHeight={maxHeight}>
                            {options.map((option, index) => {
                                const isActive = multiple
                                    ? Array.isArray(_innerState) && _innerState.includes(option.value as T)
                                    : _innerState === option.value;
                                return (
                                    <ListItem
                                        key={`option-${option.value.replaceAll(' ', '')}-${index}`}
                                        $active={isActive}
                                        $rich={!showCheckbox}
                                        onClick={(e) => {
                                            if (!showCheckbox) {
                                                e.stopPropagation();
                                                if (multiple) {
                                                    handleInputChange(option.value as T, isActive);
                                                } else {
                                                    handleInputChange(option.value as T);
                                                }
                                            }
                                        }}
                                    >
                                        {showCheckbox ? (
                                            multiple ? (
                                                <Checkbox
                                                    checked={isActive}
                                                    onChange={(checked) => handleInputChange(option.value as T, !checked)}
                                                    label={<StyledCheckboxLabel>{option.label}</StyledCheckboxLabel>}
                                                />
                                            ) : (
                                        <FilterRadio
                                                    name='type'
                                                    label={option.label}
                                                    value={option.value}
                                                    checked={isActive}
                                                    onSelect={(val: string | number) => handleInputChange(val as T)}
                                                />
                                            )
                                        ) : (
                                            option.label
                                        )}
                                    </ListItem>
                                );
                            })}
                        </List>
                        {clearable && <ClearFooter onClick={handleClearing}>Clear</ClearFooter>}
                    </PopoverBody>
                </Popover>
            )}
        </Container>
    );

    return disabled && disabledTooltip ? <div title={disabledTooltip}>{SelectContent}</div> : SelectContent;
};
